"use client";

import type { Dispatch, RefObject, SetStateAction } from "react";
import {
  selectZoomButtonState,
  type GanttZoomButtonState,
} from "./gantt-render-selectors";
import type { GanttLayout } from "./gantt-types";
import { useGanttAnchoredZoom } from "./useGanttAnchoredZoom";
import { useGanttZoomActions } from "./useGanttZoomActions";
import { useGanttZoomLatestRefs } from "./useGanttZoomLatestRefs";
import { useGanttZoomPointerListeners } from "./useGanttZoomPointerListeners";

export interface UseGanttZoomControlsParams {
  fitPxPerDay: number;
  effectivePxPerDay: number;
  pxPerDayOverride: number | null;
  setPxPerDayOverride: Dispatch<SetStateAction<number | null>>;
  layout: GanttLayout | null;
  timelineScrollRef: RefObject<HTMLDivElement | null>;
}

export interface UseGanttZoomControlsResult extends GanttZoomButtonState {
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleScrollToToday: () => void;
}

export function useGanttZoomControls({
  fitPxPerDay,
  effectivePxPerDay,
  pxPerDayOverride,
  setPxPerDayOverride,
  layout,
  timelineScrollRef,
}: UseGanttZoomControlsParams): UseGanttZoomControlsResult {
  const { fitPxPerDayRef, pxPerDayOverrideRef } = useGanttZoomLatestRefs({
    fitPxPerDay,
    pxPerDayOverride,
  });
  const applyZoomAtAnchor = useGanttAnchoredZoom({
    fitPxPerDayRef,
    setPxPerDayOverride,
    timelineScrollRef,
  });
  const { handleZoomIn, handleZoomOut, handleScrollToToday } =
    useGanttZoomActions({
      fitPxPerDay,
      layout,
      setPxPerDayOverride,
      timelineScrollRef,
    });

  useGanttZoomPointerListeners({
    applyZoomAtAnchor,
    fitPxPerDayRef,
    isEnabled: !!layout,
    pxPerDayOverrideRef,
    timelineScrollRef,
  });

  const zoomButtonState = selectZoomButtonState({
    effectivePxPerDay,
  });

  return {
    ...zoomButtonState,
    handleZoomIn,
    handleZoomOut,
    handleScrollToToday,
  };
}
